import { FC, useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import ErrorMessage from "@/components/ErrorMessage"
import Loader from "@/components/Loader"
import { Routes } from "@/core/routing/routes"
import {
  useInboxingCallbackTokenMutation,
  useInboxingHandoffMutation,
} from "@/core/api/services"
import { loginUserAction } from "@/redux/authSlice"
import { useAppDispatch } from "@/redux/hooks"

type CallbackPayload = {
  callbackToken: string
  callbackParam: "token" | "code"
}

type HandoffPayload = {
  inboxingOrgId: string
  inboxingUserId: string
  email: string
  signature?: string
  firstName?: string
  lastName?: string
  orgName?: string
  organizationName?: string
  orgUrl?: string
  role?: string
  timestamp?: string
  issuedAt?: string
  iat?: string
  expiresAt?: string
  exp?: string
}

type OAuthPayload = CallbackPayload | HandoffPayload

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const readString = (source: Record<string, unknown>, keys: string[]) => {
  const value = keys
    .map((key) => source[key])
    .find((item) => item !== undefined && item !== null)
  const normalized =
    value === undefined || value === null ? "" : String(value).trim()

  return normalized || undefined
}

const callbackPayloadFromRecord = (
  source: Record<string, unknown>,
): CallbackPayload | null => {
  const token = readString(source, ["token"])
  if (token) return { callbackToken: token, callbackParam: "token" }

  const code = readString(source, ["code"])
  if (code) return { callbackToken: code, callbackParam: "code" }

  const callbackToken = readString(source, ["callbackToken"])
  if (!callbackToken) return null

  return {
    callbackToken,
    callbackParam:
      readString(source, ["callbackParam"])?.toLowerCase() === "code"
        ? "code"
        : "token",
  }
}

const handoffPayloadFromRecord = (
  source: Record<string, unknown>,
): HandoffPayload | null => {
  const inboxingOrgId = readString(source, ["inboxingOrgId", "orgId"])
  const inboxingUserId = readString(source, ["inboxingUserId", "userId"])
  const email = readString(source, ["email"])
  if (!inboxingOrgId || !inboxingUserId || !email) return null

  return {
    inboxingOrgId,
    inboxingUserId,
    email,
    signature: readString(source, ["signature"]),
    firstName: readString(source, ["firstName"]),
    lastName: readString(source, ["lastName"]),
    orgName: readString(source, ["orgName"]),
    organizationName: readString(source, ["organizationName"]),
    orgUrl: readString(source, ["orgUrl"]),
    role: readString(source, ["role"]),
    timestamp: readString(source, ["timestamp"]),
    issuedAt: readString(source, ["issuedAt"]),
    iat: readString(source, ["iat"]),
    expiresAt: readString(source, ["expiresAt"]),
    exp: readString(source, ["exp"]),
  }
}

const decodedPayloadFromParam = (encodedPayload: string) => {
  try {
    const normalized = encodedPayload
      .trim()
      .replace(/-/g, "+")
      .replace(/_/g, "/")
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    )

    const payload = JSON.parse(window.atob(padded))
    return isRecord(payload) ? payload : null
  } catch {
    return null
  }
}

const isCallbackPayload = (payload: OAuthPayload): payload is CallbackPayload =>
  "callbackToken" in payload

const parsePayload = (params: URLSearchParams): OAuthPayload | null => {
  const queryPayload = Object.fromEntries(params.entries())
  const queryCallback = callbackPayloadFromRecord(queryPayload)
  if (queryCallback) return queryCallback

  const encodedPayload = params.get("payload")
  if (encodedPayload) {
    const decodedPayload = decodedPayloadFromParam(encodedPayload)
    if (!decodedPayload) return null

    return (
      callbackPayloadFromRecord(decodedPayload) ||
      handoffPayloadFromRecord(decodedPayload)
    )
  }

  return handoffPayloadFromRecord(queryPayload)
}

const sanitizeCallbackUrl = () => {
  if (typeof window === "undefined") return

  const url = new URL(window.location.href)
  const sensitiveKeys = [
    "payload",
    "signature",
    "token",
    "code",
    "callbackToken",
    "callbackParam",
  ]
  sensitiveKeys.forEach((key) => {
    url.searchParams.delete(key)
  })

  const sanitized = `${url.pathname}${url.search}${url.hash}`
  window.history.replaceState({}, document.title, sanitized)
}

const OAuthRedirectPage: FC = () => {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [error, setError] = useState(false)
  const [handoff] = useInboxingHandoffMutation()
  const [exchangeCallbackToken] = useInboxingCallbackTokenMutation()
  const payload = useMemo(() => parsePayload(params), [params])

  // Google/Microsoft "connect account" buttons use react-simple-oauth2-login,
  // which opens this route in a popup and reads the authorization code by
  // polling the popup's URL. In that case we must not consume the code,
  // sanitize the URL, or navigate away, or the opener never receives it.
  // The Inboxing login callback is a full-page redirect (no opener).
  const isOAuthPopupCapture = useMemo(() => {
    if (typeof window === "undefined") return false
    const hasOpener = Boolean(window.opener) && window.opener !== window
    return hasOpener && params.has("code") && !params.has("payload")
  }, [params])

  useEffect(() => {
    if (isOAuthPopupCapture) return

    if (!payload) {
      setError(true)
      return
    }

    sanitizeCallbackUrl()

    const request = isCallbackPayload(payload)
      ? exchangeCallbackToken(
          payload.callbackParam === "token"
            ? { token: payload.callbackToken }
            : { code: payload.callbackToken },
        )
      : handoff(payload)

    request
      .unwrap()
      .then((user) => {
        dispatch(loginUserAction(user))
        navigate(Routes.organizations, { replace: true })
      })
      .catch(() => setError(true))
  }, [
    dispatch,
    exchangeCallbackToken,
    handoff,
    navigate,
    payload,
    isOAuthPopupCapture,
  ])

  if (isOAuthPopupCapture) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <Loader />
      </div>
    )
  }

  if (error) return <ErrorMessage />

  return (
    <div className="h-screen w-screen flex items-center justify-center">
      <Loader />
    </div>
  )
}

export default OAuthRedirectPage
